import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { Mic, FileText, Mail, ArrowRight, Headphones, Youtube, ExternalLink } from 'lucide-react'
import ecosystem from '../data/ecosystem.json'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

export default function ContentHub() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const { podcast, newsletter, blog } = ecosystem.media

  return (
    <section id="content" className="relative py-20 overflow-hidden bg-white lg:py-32">
      {/* Background */}
      <div className="absolute top-1/2 left-0 w-96 h-96 -translate-y-1/2 -translate-x-1/2 rounded-full bg-rose-valley-100/30 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-72 h-72 translate-x-1/2 translate-y-1/2 rounded-full bg-primary-100/30 blur-3xl" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Contenu & <span className="gradient-text">Médias</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Découvrez mes réflexions et partages à travers différents formats
            </p>
          </motion.div>

          {/* Content Grid */}
          <div className="grid gap-8 mt-16 lg:grid-cols-3">
            {/* Podcast Card */}
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="relative overflow-hidden transition-shadow border border-gray-100 shadow-sm group rounded-3xl bg-gradient-to-br from-rose-valley-50 to-rose-valley-100/50 hover:shadow-xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-valley-500 to-rose-valley-700">
                  <Mic className="w-8 h-8 text-white" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-gray-900">{podcast.name}</h3>
                <p className="mt-1 text-sm font-medium text-rose-valley-600">{podcast.tagline}</p>
                
                <p className="mt-4 text-gray-600">{podcast.description}</p>

                {/* Platform Links */}
                <div className="flex items-center gap-3 mt-6">
                  <a
                    href={podcast.platforms.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-white/80 text-gray-600 hover:bg-white hover:text-red-600"
                    title="YouTube"
                  >
                    <Youtube className="w-5 h-5" />
                  </a>
                  <a
                    href={podcast.platforms.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-white/80 text-gray-600 hover:bg-white hover:text-green-600"
                    title="Spotify"
                  >
                    <Headphones className="w-5 h-5" />
                  </a>
                  <a
                    href={podcast.platforms.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-white/80 text-gray-600 hover:bg-white hover:text-purple-600"
                    title="Apple Podcasts"
                  >
                    <Mic className="w-5 h-5" />
                  </a>
                </div>

                <Link
                  to="/podcast"
                  className="inline-flex items-center gap-2 mt-6 font-semibold transition-colors text-rose-valley-700 hover:text-rose-valley-900"
                >
                  Voir tous les épisodes
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* Newsletter Card */}
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="relative overflow-hidden transition-shadow border border-gray-100 shadow-sm group rounded-3xl bg-gradient-to-br from-amber-50 to-amber-100/50 hover:shadow-xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700">
                  <Mail className="w-8 h-8 text-white" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-gray-900">{newsletter.name}</h3>
                <p className="mt-1 text-sm font-medium text-amber-600">{newsletter.tagline}</p>
                
                <p className="mt-4 text-gray-600">{newsletter.description}</p>

                <div className="flex items-center gap-2 mt-6 text-sm text-gray-500">
                  <span className="px-2 py-1 rounded-full bg-white/80">{newsletter.frequency}</span>
                </div>

                <a
                  href={newsletter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 font-semibold transition-colors text-amber-700 hover:text-amber-900"
                >
                  S'inscrire à la newsletter
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </motion.div>

            {/* Blog Card */}
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="relative overflow-hidden transition-shadow border border-gray-100 shadow-sm group rounded-3xl bg-gradient-to-br from-indigo-50 to-indigo-100/50 hover:shadow-xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700">
                  <FileText className="w-8 h-8 text-white" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-gray-900">{blog.name}</h3>
                <p className="mt-1 text-sm font-medium text-indigo-600">Tech, Cloud & Entrepreneuriat</p>
                
                <p className="mt-4 text-gray-600">{blog.description}</p>

                {/* Topics */}
                <div className="flex flex-wrap gap-2 mt-6">
                  {blog.sources[0]?.topics.map((topic) => (
                    <span
                      key={topic}
                      className="px-2 py-1 text-xs rounded-full bg-white/80 text-gray-600"
                    >
                      {topic}
                    </span>
                  ))}
                </div>

                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 mt-6 font-semibold transition-colors text-indigo-700 hover:text-indigo-900"
                >
                  Lire les articles
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
